// src/components/VeiculoEditModal.jsx
import api from '../services/api';
import { toast } from 'react-toastify';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FaSave,
  FaPlus,
  FaTrash,
  FaDownload,
  FaCar,
  FaTools,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaTimes,
  FaFileInvoice,
  FaPaperclip,
  FaSpinner,
  FaGasPump,
  FaMobile,
  FaHistory,
  FaUser,
  FaCheckCircle,
  FaClock,
  FaTruck,
  FaPlayCircle,
  FaStopCircle,
  FaTachometerAlt,
  FaMapMarkerAlt,
  FaChartLine,
  FaPaperPlane,
  FaExclamationTriangle,
  FaWrench,
  FaBuilding,
  FaRoad,
} from 'react-icons/fa';
import './VeiculoEditModal.css';
import ManutencaoRelatorioModal from './ManutencaoRelatorioModal';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de formatação
// ─────────────────────────────────────────────────────────────────────────────
const formatCurrency = (val) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

const formatDecimalBR = (val, casas = 2) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
};

const formatKm = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${n.toLocaleString('pt-BR')} km`;
};

const formatKmL = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${formatDecimalBR(n, 2)} km/L`;
};

const formatDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  return isNaN(d)
    ? val
    : d.toLocaleDateString('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  return isNaN(d)
    ? val
    : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const normalizeDriverId = (id) => {
  if (id === null || id === undefined || id === '') return '';
  return String(id).replace('MOT-', '').replace('USR-', '').trim();
};

const getNowLocalDate = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const getNowLocalDateTimeInput = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

// ─────────────────────────────────────────────────────────────────────────────
// Badges reutilizáveis
// ─────────────────────────────────────────────────────────────────────────────
function OsStatusBadge({ status }) {
  const map = {
    Concluído:    { cls: 'paga',     label: 'Concluído' },
    'Em Andamento': { cls: 'aprovada', label: 'Em Andamento' },
    Cancelado:    { cls: 'rejeitada', label: 'Cancelado' },
    Agendado:     { cls: 'pendente', label: 'Agendado' },
  };
  const c = map[status] || { cls: 'pendente', label: status };
  return <span className={`vec-origem-badge vec-origem-badge--status-${c.cls}`}>{c.label}</span>;
}

function MfStatusBadge({ status }) {
  const map = {
    concluido:           { cls: 'paga',     label: 'Concluído' },
    aprovado:            { cls: 'aprovada', label: 'Aprovado' },
    aguardando_orcamento:{ cls: 'pendente', label: 'Aguardando orçamento' },
    em_analise:          { cls: 'pendente', label: 'Em análise' },
    pendente:            { cls: 'pendente', label: 'Pendente' },
    recusado:            { cls: 'rejeitada',label: 'Recusado' },
  };
  const c = map[status] || { cls: 'pendente', label: status || '—' };
  return <span className={`vec-origem-badge vec-origem-badge--status-${c.cls}`}>{c.label}</span>;
}

function OrigemBadge({ origem }) {
  if (origem === 'app')
    return <span className="vec-origem-badge vec-origem-badge--app"><FaMobile style={{ marginRight: 3 }} /> App</span>;
  if (origem === 'combustivel')
    return <span className="vec-origem-badge vec-origem-badge--fuel"><FaGasPump style={{ marginRight: 3 }} /> Comb.</span>;
  if (origem === 'oficina')
    return <span className="vec-origem-badge vec-origem-badge--oficina"><FaWrench style={{ marginRight: 3 }} /> Oficina</span>;
  return null;
}

function StatusBadge({ status, origem }) {
  if (!status || origem === 'combustivel' || origem === 'oficina') return null;
  const map   = { Pendente: 'pendente', Aprovada: 'aprovada', Paga: 'paga', Rejeitada: 'rejeitada' };
  const label = { Pendente: 'Pendente', Aprovada: 'Aprovada', Paga: 'Pago', Rejeitada: 'Rejeitado' };
  const cls   = map[status] || 'pendente';
  return <span className={`vec-origem-badge vec-origem-badge--status-${cls}`}>{label[status] || status}</span>;
}

// Badge de tipo de uso do extrato Sem Parar
function SpTipoUsoBadge({ tipo }) {
  const map = {
    'PASSAGENS':        { color: '#60a5fa', bg: 'rgba(37,99,235,0.12)',   label: '🛣️ Pedágio' },
    'ESTACIONAMENTO':   { color: '#34d399', bg: 'rgba(5,150,105,0.12)',   label: '🅿️ Estacionamento' },
    'PLANO CONTRATADO': { color: '#c084fc', bg: 'rgba(109,40,217,0.12)', label: '📋 Plano' },
    'ABASTECIMENTO':    { color: '#fbbf24', bg: 'rgba(146,64,14,0.12)',   label: '⛽ Combustível' },
  };
  const cfg = map[tipo] || { color: '#94a3b8', bg: 'rgba(148,163,184,0.08)', label: tipo || '?' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: cfg.bg, color: cfg.color,
      padding: '2px 10px', borderRadius: 999,
      fontSize: '0.72rem', fontWeight: 600,
      border: `1px solid ${cfg.color}38`,
      letterSpacing: '0.01em',
    }}>
      {cfg.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Estado inicial do Sem Parar
// ─────────────────────────────────────────────────────────────────────────────
const SP_EMPTY = { rows: [], totais_por_tipo: {}, total_geral: 0, total_registros: 0 };

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
export default function VeiculoEditModal({ veiculo, motoristas, onClose, onSave }) {
  const [activeTab, setActiveTab] = useState('dados');
  const [formData, setFormData]   = useState({
    ...veiculo,
    motorista_id: normalizeDriverId(veiculo?.motorista_id || ''),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Aba Histórico & Despesas ──
  const [historicoManutencao, setHistoricoManutencao] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory]       = useState(true);
  const [newMaintenance, setNewMaintenance]           = useState({ data: getNowLocalDate(), tipo: '', custo: '', descricao: '' });
  const [anexo, setAnexo]                             = useState(null);

  // ── Aba Logs de Uso — dados ──
  const [logsUso, setLogsUso]           = useState({ os: [], prestacoes: [], abastecimentos: [] });
  const [usoDiario, setUsoDiario]       = useState([]);
  const [semPararData, setSemPararData] = useState(SP_EMPTY);
  const [manutencoesFrota, setManutencoesFrota] = useState([]);

  // ── Aba Logs de Uso — loadings ──
  const [isLoadingLogs, setIsLoadingLogs]                   = useState(false);
  const [isLoadingUsoDiario, setIsLoadingUsoDiario]         = useState(false);
  const [isLoadingSemParar, setIsLoadingSemParar]           = useState(false);
  const [isLoadingManutencoesFrota, setIsLoadingManutencoesFrota] = useState(false);

  // ── Sub-aba ativa nos Logs ──
  const [logSubTab, setLogSubTab] = useState('os');

  // ── Formulário Uso Diário ──
  const [novoUso, setNovoUso]           = useState({ motorista_id: '', data_uso: getNowLocalDate(), entrada_em: getNowLocalDateTimeInput(), saida_em: '', observacao: '' });
  const [isSubmittingUso, setIsSubmittingUso] = useState(false);
  const [pingingUsoIds, setPingingUsoIds]     = useState({});
  const [pingSentUsoIds, setPingSentUsoIds]   = useState({});

  // ── Relatório de manutenção (modal aninhado) ──
  const [manutencaoRelatorio, setManutencaoRelatorio] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────
  // motoristas normalizados
  // ─────────────────────────────────────────────────────────────────────────
  const motoristasNormalizados = useMemo(() =>
    (motoristas || []).map((m) => ({
      ...m,
      id_normalizado: normalizeDriverId(m.id),
      label_extra: m.origem === 'operacional' ? ' (Operacional)' : m.origem === 'motorista' ? ' (Motorista)' : '',
      optionKey: m.id_raw || m.id,
    })),
  [motoristas]);

  // ─────────────────────────────────────────────────────────────────────────
  // Fetches
  // ─────────────────────────────────────────────────────────────────────────
  const fetchMaintenanceHistory = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoadingHistory(true);
    try {
      const { data } = await api.get(`/api/vehicles/${veiculo.id}/details`);
      setHistoricoManutencao(data.historico_manutencao || []);
    } catch { if (!silent) toast.error('Falha ao buscar histórico.'); }
    finally  { if (!silent) setIsLoadingHistory(false); }
  }, [veiculo.id]);

  const fetchLogsUso = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoadingLogs(true);
    try {
      const { data } = await api.get(`/api/vehicles/${veiculo.id}/logs-uso`);
      setLogsUso({ os: data?.os || [], prestacoes: data?.prestacoes || [], abastecimentos: data?.abastecimentos || [] });
    } catch { if (!silent) toast.error('Falha ao buscar logs de uso.'); }
    finally  { if (!silent) setIsLoadingLogs(false); }
  }, [veiculo.id]);

  const fetchUsoDiario = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoadingUsoDiario(true);
    try {
      const { data } = await api.get(`/api/vehicles/${veiculo.id}/uso-diario`);
      setUsoDiario(Array.isArray(data) ? data : []);
    } catch {
      if (!silent) { setUsoDiario([]); toast.error('Falha ao buscar uso diário.'); }
    }
    finally { if (!silent) setIsLoadingUsoDiario(false); }
  }, [veiculo.id]);

  const fetchSemParar = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoadingSemParar(true);
    try {
      const { data } = await api.get(`/api/vehicles/${veiculo.id}/sem-parar`);
      setSemPararData({
        rows:            data?.rows            || [],
        totais_por_tipo: data?.totais_por_tipo || {},
        total_geral:     data?.total_geral     || 0,
        total_registros: data?.total_registros || 0,
      });
    } catch {
      if (!silent) { setSemPararData(SP_EMPTY); toast.error('Falha ao buscar lançamentos Sem Parar.'); }
    }
    finally { if (!silent) setIsLoadingSemParar(false); }
  }, [veiculo.id]);

  const fetchManutencoesFrota = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoadingManutencoesFrota(true);
    try {
      const { data } = await api.get(`/api/vehicles/${veiculo.id}/manutencoes-frota`);
      setManutencoesFrota(Array.isArray(data) ? data : []);
    } catch {
      if (!silent) { setManutencoesFrota([]); toast.error('Falha ao buscar manutenções.'); }
    }
    finally { if (!silent) setIsLoadingManutencoesFrota(false); }
  }, [veiculo.id]);

  // ─────────────────────────────────────────────────────────────────────────
  // Effects
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab === 'manutencoes') fetchMaintenanceHistory();
    if (activeTab === 'logs') {
      if      (logSubTab === 'uso_diario')       fetchUsoDiario();
      else if (logSubTab === 'manutencoes_frota') fetchManutencoesFrota();
      else if (logSubTab === 'sem_parar')         fetchSemParar();
      else                                         fetchLogsUso();
    }
  }, [activeTab, logSubTab, fetchMaintenanceHistory, fetchLogsUso, fetchUsoDiario, fetchManutencoesFrota, fetchSemParar]);

  // Background polling — apenas OS / Prestações / Abastecimentos / Uso Diário
  useEffect(() => {
    if (activeTab !== 'logs')                    return undefined;
    if (logSubTab === 'manutencoes_frota')       return undefined; // dado estático — sem polling
    if (logSubTab === 'sem_parar')               return undefined; // dado estático — sem polling

    const refresh = async () => {
      try {
        await fetchUsoDiario({ silent: true });
        await fetchLogsUso({ silent: true });
      } catch { /* silencioso */ }
    };

    refresh();
    const id = setInterval(refresh, logSubTab === 'uso_diario' ? 4000 : 8000);
    return () => clearInterval(id);
  }, [activeTab, logSubTab, fetchUsoDiario, fetchLogsUso]);

  // ─────────────────────────────────────────────────────────────────────────
  // Memos
  // ─────────────────────────────────────────────────────────────────────────
  const totalGastoManutencao  = useMemo(() => historicoManutencao.reduce((a, m) => a + (parseFloat(m.custo) || 0), 0), [historicoManutencao]);
  const usosEmAberto          = useMemo(() => usoDiario.filter((u) => !u.saida_em).length, [usoDiario]);
  const totalManutencoesFrota = useMemo(() => manutencoesFrota.reduce((a, m) => a + (parseFloat(m.valor_final) || 0), 0), [manutencoesFrota]);

  const fuelStats = useMemo(() => {
    const list       = logsUso.abastecimentos || [];
    const totalLitros = list.reduce((a, x) => a + (parseFloat(x.litros) || 0), 0);
    const totalGasto  = list.reduce((a, x) => a + (parseFloat(x.valor)  || 0), 0);
    const comOdo = list
      .filter((x) => Number(x.odometro_registrado) > 0)
      .map((x) => ({ odo: Number(x.odometro_registrado), litros: parseFloat(x.litros) || 0 }))
      .sort((a, b) => a.odo - b.odo);
    let consumo = 0;
    if (comOdo.length >= 2) {
      const dist   = comOdo[comOdo.length - 1].odo - comOdo[0].odo;
      const litros = comOdo.slice(1).reduce((a, x) => a + x.litros, 0);
      consumo = litros > 0 ? dist / litros : 0;
    }
    return { totalLitros, totalGasto, consumo, temConsumo: comOdo.length >= 2 && consumo > 0 };
  }, [logsUso.abastecimentos]);

  // KPIs Sem Parar por tipo
  const spTotais = useMemo(() => {
    const t = semPararData.totais_por_tipo || {};
    return {
      pedagios:  { count: t['PASSAGENS']?.count        || 0, total: t['PASSAGENS']?.total        || 0 },
      estac:     { count: t['ESTACIONAMENTO']?.count   || 0, total: t['ESTACIONAMENTO']?.total   || 0 },
      plano:     { count: t['PLANO CONTRATADO']?.count || 0, total: t['PLANO CONTRATADO']?.total || 0 },
      abastec:   { count: t['ABASTECIMENTO']?.count    || 0, total: t['ABASTECIMENTO']?.total    || 0 },
    };
  }, [semPararData.totais_por_tipo]);

  // ─────────────────────────────────────────────────────────────────────────
  // Handlers — Dados Cadastrais
  // ─────────────────────────────────────────────────────────────────────────
  const handleDataChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveGeneralData = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.put(`/api/vehicles/${formData.id}`, {
        ...formData,
        motorista_id: formData.motorista_id ? Number(normalizeDriverId(formData.motorista_id)) : null,
      });
      toast.success('Dados atualizados!');
      onSave?.();
    } catch { toast.error('Erro ao salvar.'); }
    finally  { setIsSubmitting(false); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Handlers — Manutenções / Despesas
  // ─────────────────────────────────────────────────────────────────────────
  const handleMaintenanceChange = (e) => {
    const { name, value } = e.target;
    setNewMaintenance((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => { if (e.target.files?.[0]) setAnexo(e.target.files[0]); };

  const handleAddMaintenance = async (e) => {
    e.preventDefault();
    if (!newMaintenance.custo || parseFloat(newMaintenance.custo) <= 0) { toast.warning('Informe um custo válido.'); return; }
    setIsSubmitting(true);
    const fd = new FormData();
    Object.keys(newMaintenance).forEach((k) => fd.append(k, newMaintenance[k]));
    if (anexo) fd.append('anexo', anexo);
    try {
      await api.post(`/api/vehicles/${veiculo.id}/manutencoes`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Despesa registrada!');
      setNewMaintenance({ data: getNowLocalDate(), tipo: '', custo: '', descricao: '' });
      setAnexo(null);
      await fetchMaintenanceHistory();
      onSave?.();
    } catch { toast.error('Erro ao lançar manutenção.'); }
    finally  { setIsSubmitting(false); }
  };

  const handleDeleteMaintenance = async (id) => {
    if (!window.confirm('Excluir este registro?')) return;
    try {
      await api.delete(`/api/vehicles/manutencoes/${id}`);
      toast.success('Removido.');
      await fetchMaintenanceHistory();
      onSave?.();
    } catch { toast.error('Erro ao excluir.'); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Handlers — Abastecimentos
  // ─────────────────────────────────────────────────────────────────────────
  const handleDeleteAbastecimento = async (id) => {
    if (!window.confirm('Excluir este abastecimento?')) return;
    try {
      await api.delete(`/api/vehicles/abastecimentos/${id}`);
      toast.success('Abastecimento removido.');
      await fetchLogsUso();
      onSave?.();
    } catch (err) { toast.error(err?.response?.data?.error || 'Falha ao excluir abastecimento.'); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Handlers — Sem Parar
  // ─────────────────────────────────────────────────────────────────────────
  const handleDeleteSemParar = async (id) => {
    if (!window.confirm('Excluir este lançamento Sem Parar?')) return;
    try {
      await api.delete(`/api/vehicles/sem-parar/${id}`);
      toast.success('Lançamento excluído.');
      await fetchSemParar();
    } catch (err) { toast.error(err?.response?.data?.error || 'Falha ao excluir lançamento.'); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Handlers — Uso Diário
  // ─────────────────────────────────────────────────────────────────────────
  const handleNovoUsoChange = (e) => {
    const { name, value } = e.target;
    setNovoUso((prev) => ({ ...prev, [name]: value }));
  };

  const resetNovoUso = () => setNovoUso({ motorista_id: '', data_uso: getNowLocalDate(), entrada_em: getNowLocalDateTimeInput(), saida_em: '', observacao: '' });

  const handleAddUsoDiario = async (e) => {
    e.preventDefault();
    if (!novoUso.motorista_id || !novoUso.data_uso || !novoUso.entrada_em) { toast.warning('Motorista, data e entrada são obrigatórios.'); return; }
    setIsSubmittingUso(true);
    try {
      await api.post(`/api/vehicles/${veiculo.id}/uso-diario`, {
        motorista_id: Number(normalizeDriverId(novoUso.motorista_id)),
        data_uso:     novoUso.data_uso,
        entrada_em:   novoUso.entrada_em,
        saida_em:     novoUso.saida_em  || null,
        observacao:   novoUso.observacao || null,
      });
      toast.success('Registro de uso diário salvo!');
      resetNovoUso();
      await fetchUsoDiario();
      await fetchLogsUso({ silent: true });
      onSave?.();
    } catch { toast.error('Falha ao salvar uso diário.'); }
    finally  { setIsSubmittingUso(false); }
  };

  const handleDeleteUsoDiario = async (usoId) => {
    if (!window.confirm('Excluir este registro de uso diário?')) return;
    try {
      await api.delete(`/api/vehicles/uso-diario/${usoId}`);
      toast.success('Registro excluído.');
      await fetchUsoDiario();
      await fetchLogsUso({ silent: true });
      onSave?.();
    } catch { toast.error('Falha ao excluir registro.'); }
  };

  const handleRegistrarSaidaAgora = async (uso) => {
    try {
      await api.put(`/api/vehicles/uso-diario/${uso.id}/fechar`);
      toast.success('Saída registrada com sucesso!');
      await fetchUsoDiario();
      await fetchLogsUso({ silent: true });
      onSave?.();
    } catch (err) { toast.error(err?.response?.data?.error || 'Falha ao registrar saída.'); }
  };

  const handleEnviarPingUso = async (uso) => {
    if (!uso?.id) return;
    setPingingUsoIds((prev) => ({ ...prev, [uso.id]: true }));
    try {
      const nome = String(uso.motorista_nome || '').split(' ')[0] || 'motorista';
      await api.post(`/api/vehicles/uso-diario/${uso.id}/ping`, {
        mensagem: `Olá, ${nome}! Você ainda está usando este veículo? Confirme ou registre sua saída no app.`,
      });
      toast.success('Mensagem enviada ao motorista!');
      setPingSentUsoIds((prev) => ({ ...prev, [uso.id]: true }));
      setTimeout(() => {
        setPingSentUsoIds((prev) => { const c = { ...prev }; delete c[uso.id]; return c; });
      }, 9000);
      await fetchUsoDiario({ silent: true });
      await fetchLogsUso({ silent: true });
    } catch (err) { toast.error(err?.response?.data?.error || 'Falha ao enviar mensagem.'); }
    finally {
      setPingingUsoIds((prev) => { const c = { ...prev }; delete c[uso.id]; return c; });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers de loading por sub-aba
  // ─────────────────────────────────────────────────────────────────────────
  const isLoadingCurrentSubTab =
    (logSubTab === 'uso_diario'       && isLoadingUsoDiario)         ||
    (logSubTab === 'manutencoes_frota'&& isLoadingManutencoesFrota)  ||
    (logSubTab === 'sem_parar'        && isLoadingSemParar)          ||
    (['os','prestacoes','abastecimentos'].includes(logSubTab) && isLoadingLogs);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="vec-edit-overlay" onClick={onClose}>
      <div className="vec-edit-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <header className="vec-edit-header">
          <div className="vec-edit-title">
            <div className="vec-edit-icon-box"><FaCar /></div>
            <div className="vec-edit-title-text">
              <div className="vec-edit-title-row">
                <h2>Ficha do Veículo</h2>
                {veiculo.placa && (
                  <span className="vec-edit-placa-plate">
                    <span className="vec-edit-placa-plate__bar">BRASIL</span>
                    <span className="vec-edit-placa-plate__num">{veiculo.placa}</span>
                  </span>
                )}
              </div>
              <span className="vec-edit-subtitle">
                {veiculo.marca} {veiculo.modelo}{veiculo.ano ? ` · ${veiculo.ano}` : ''}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="vec-btn-close"><FaTimes /></button>
        </header>

        {/* ── Abas principais ── */}
        <div className="vec-edit-tabs">
          <button className={`vec-tab-btn ${activeTab === 'dados' ? 'active' : ''}`} onClick={() => setActiveTab('dados')}>
            <FaFileInvoice /> Dados Cadastrais
          </button>
          <button className={`vec-tab-btn ${activeTab === 'manutencoes' ? 'active' : ''}`} onClick={() => setActiveTab('manutencoes')}>
            <FaTools /> Histórico & Despesas
          </button>
          <button className={`vec-tab-btn ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveTab('logs')}>
            <FaHistory /> Logs de Uso
          </button>
        </div>

        <div className="vec-edit-body">

          {/* ════════════════════════════════════════════════
              ABA: DADOS CADASTRAIS
          ════════════════════════════════════════════════ */}
          {activeTab === 'dados' && (
            <form onSubmit={handleSaveGeneralData} className="vec-form animate-fade-in">
              <div className="vec-form-highlight-row">
                <div className="vec-input-group">
                  <label>Placa (Padrão Mercosul)</label>
                  <div className="vec-placa-input-wrapper">
                    <div className="vec-placa-top-bar">BRASIL</div>
                    <input
                      type="text" name="placa" required className="vec-placa-input" maxLength="7"
                      value={formData.placa}
                      onChange={(e) => setFormData({ ...formData, placa: e.target.value.toUpperCase() })}
                    />
                  </div>
                </div>
                <div className="vec-input-group">
                  <label>Modelo do Veículo</label>
                  <input type="text" name="modelo" value={formData.modelo} onChange={handleDataChange} />
                </div>
                <div className="vec-input-group">
                  <label>Marca Fabricante</label>
                  <input type="text" name="marca" value={formData.marca} onChange={handleDataChange} />
                </div>
              </div>

              <div className="vec-form-grid" style={{ marginTop: 20 }}>
                <div className="vec-input-group">
                  <label>Ano de Fabricação</label>
                  <input type="number" name="ano" value={formData.ano} onChange={handleDataChange} />
                </div>
                <div className="vec-input-group">
                  <label>Status Operacional</label>
                  <select name="status" value={formData.status} onChange={handleDataChange}>
                    <option value="Disponível">Disponível</option>
                    <option value="Em Serviço">Em Serviço</option>
                    <option value="Manutenção">Manutenção</option>
                  </select>
                </div>
                <div className="vec-input-group">
                  <label>Motorista Fixo (Opcional)</label>
                  <select name="motorista_id" value={formData.motorista_id || ''} onChange={handleDataChange}>
                    <option value="">Nenhum Motorista</option>
                    {motoristasNormalizados.map((m) => (
                      <option key={m.optionKey} value={m.id_normalizado}>{m.nome}{m.label_extra}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="vec-edit-footer">
                <button type="button" className="vec-btn-outline" onClick={onClose} disabled={isSubmitting}>Cancelar Edição</button>
                <button type="submit" className="vec-btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? <><FaSpinner className="fa-spin" /> Salvando...</> : <><FaSave /> Salvar Alterações</>}
                </button>
              </div>
            </form>
          )}

          {/* ════════════════════════════════════════════════
              ABA: LOGS DE USO
          ════════════════════════════════════════════════ */}
          {activeTab === 'logs' && (
            <div className="vec-logs-wrapper animate-fade-in">

              {/* ── Sub-abas ── */}
              <div className="vec-log-subtabs">
                <button className={`vec-log-subtab ${logSubTab === 'os' ? 'active' : ''}`} onClick={() => setLogSubTab('os')}>
                  <FaTruck /> OS
                  {logsUso.os.length > 0 && <span className="vec-log-count">{logsUso.os.length}</span>}
                </button>
                <button className={`vec-log-subtab ${logSubTab === 'prestacoes' ? 'active' : ''}`} onClick={() => setLogSubTab('prestacoes')}>
                  <FaMobile /> App
                  {logsUso.prestacoes.length > 0 && <span className="vec-log-count">{logsUso.prestacoes.length}</span>}
                </button>
                <button className={`vec-log-subtab ${logSubTab === 'abastecimentos' ? 'active' : ''}`} onClick={() => setLogSubTab('abastecimentos')}>
                  <FaGasPump /> Combustível
                  {logsUso.abastecimentos.length > 0 && <span className="vec-log-count">{logsUso.abastecimentos.length}</span>}
                </button>
                <button className={`vec-log-subtab ${logSubTab === 'sem_parar' ? 'active' : ''}`} onClick={() => setLogSubTab('sem_parar')}>
                  <FaRoad /> Sem Parar
                  {semPararData.total_registros > 0 && <span className="vec-log-count">{semPararData.total_registros}</span>}
                </button>
                <button className={`vec-log-subtab ${logSubTab === 'manutencoes_frota' ? 'active' : ''}`} onClick={() => setLogSubTab('manutencoes_frota')}>
                  <FaWrench /> Manutenções
                  {manutencoesFrota.length > 0 && <span className="vec-log-count">{manutencoesFrota.length}</span>}
                </button>
                <button className={`vec-log-subtab ${logSubTab === 'uso_diario' ? 'active' : ''}`} onClick={() => setLogSubTab('uso_diario')}>
                  <FaUser /> Uso Diário
                  {usoDiario.length > 0 && <span className="vec-log-count">{usoDiario.length}</span>}
                </button>
              </div>

              {/* ── Spinner global por sub-aba ── */}
              {isLoadingCurrentSubTab ? (
                <div className="vec-loading-box"><FaSpinner className="fa-spin" /></div>
              ) : (
                <>
                  {/* ══════════════════════════════════════════
                      SUB-ABA: ABASTECIMENTOS
                      (sem painel de importação — use Ações)
                  ══════════════════════════════════════════ */}
                  {logSubTab === 'abastecimentos' && (
                    <div className="vec-fuel-wrapper">
                      {/* KPIs */}
                      <div className="vec-fuel-kpis">
                        <div className="vec-fuel-kpi">
                          <FaMoneyBillWave />
                          <div><label>Total Abastecido</label><strong>{formatCurrency(fuelStats.totalGasto)}</strong></div>
                        </div>
                        <div className="vec-fuel-kpi">
                          <FaGasPump />
                          <div><label>Total de Litros</label><strong>{fuelStats.totalLitros.toFixed(2)} L</strong></div>
                        </div>
                        <div className="vec-fuel-kpi">
                          <FaChartLine />
                          <div><label>Eficiência Média</label><strong>{fuelStats.temConsumo ? formatKmL(fuelStats.consumo) : '—'}</strong></div>
                        </div>
                      </div>

                      {/* Lista em largura total */}
                      <div className="vec-maintenance-list" style={{ marginTop: 4, height: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
                        <h4 className="vec-section-title">Histórico de Abastecimentos</h4>
                        <div className="vec-history-scroll">
                          {logsUso.abastecimentos.length === 0 ? (
                            <div className="vec-empty-box">
                              Nenhum abastecimento para este veículo. Para importar, use o menu <strong>Ações → Abastecimentos</strong>.
                            </div>
                          ) : logsUso.abastecimentos.map((a) => (
                            <div key={a.id} className="vec-log-card">
                              <div className="vec-log-card__header">
                                <span className="vec-log-card__id"><FaGasPump /> Abastecimento #{a.id}</span>
                                <div className="vec-fuel-card__head-actions">
                                  <span className="vec-origem-badge vec-origem-badge--fuel">
                                    {a.origem === 'app' ? 'App' : a.origem === 'sem_parar' ? 'Sem Parar' : 'Manual'}
                                  </span>
                                  <button type="button" className="vec-icon-btn delete" title="Excluir" onClick={() => handleDeleteAbastecimento(a.id)}>
                                    <FaTrash />
                                  </button>
                                </div>
                              </div>
                              <div className="vec-log-card__body">
                                {a.motorista_nome && a.motorista_nome !== 'Não informado' && (
                                  <div className="vec-log-card__row"><FaUser className="vec-log-icon" /><span><strong>Motorista:</strong> {a.motorista_nome}</span></div>
                                )}
                                <div className="vec-log-card__row"><FaCalendarAlt className="vec-log-icon" /><span><strong>Data:</strong> {formatDate(a.data)}</span></div>
                                <div className="vec-log-card__row">
                                  <FaGasPump className="vec-log-icon" style={{ color: '#f59e0b' }} />
                                  <span>{Number(a.litros).toFixed(2)} L{a.tipo_combustivel ? ` · ${a.tipo_combustivel}` : ''}</span>
                                </div>
                                {Number(a.odometro_registrado) > 0 && (
                                  <div className="vec-log-card__row"><FaTachometerAlt className="vec-log-icon" /><span>{Number(a.odometro_registrado).toLocaleString('pt-BR')} km</span></div>
                                )}
                                <div className="vec-fuel-card-metrics">
                                  <div><label>R$/L</label><strong>{a.valor_por_litro ? formatCurrency(a.valor_por_litro) : '—'}</strong></div>
                                  <div><label>Distância</label><strong>{a.distancia_ultimo_abastecimento ? formatKm(a.distancia_ultimo_abastecimento) : '—'}</strong></div>
                                  <div><label>Eficiência</label><strong>{a.eficiencia_km_l ? formatKmL(a.eficiencia_km_l) : '—'}</strong></div>
                                </div>
                                {a.alerta_metrica && (
                                  <div className="vec-fuel-card-warning"><FaExclamationTriangle />{a.alerta_metrica}</div>
                                )}
                                {a.posto && <div className="vec-log-card__row"><FaMapMarkerAlt className="vec-log-icon" /><span>{a.posto}</span></div>}
                                {a.observacao && <div className="vec-log-card__row"><FaFileInvoice className="vec-log-icon" /><span className="vec-log-card__desc">{a.observacao}</span></div>}
                              </div>
                              <div className="vec-log-card__footer">
                                <span className="vec-log-card__valor">{formatCurrency(a.valor)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ══════════════════════════════════════════
                      SUB-ABA: SEM PARAR
                  ══════════════════════════════════════════ */}
                  {logSubTab === 'sem_parar' && (
                    <div className="vec-fuel-wrapper">

                      {/* KPIs */}
                      <div className="vec-fuel-kpis" style={{ flexWrap: 'wrap' }}>
                        <div className="vec-fuel-kpi">
                          <FaMoneyBillWave />
                          <div>
                            <label>Total Geral</label>
                            <strong>{formatCurrency(semPararData.total_geral)}</strong>
                          </div>
                        </div>
                        <div className="vec-fuel-kpi">
                          <FaRoad style={{ color: '#60a5fa' }} />
                          <div>
                            <label>Pedágios</label>
                            <strong>{formatCurrency(spTotais.pedagios.total)}</strong>
                            <small style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', display: 'block' }}>
                              {spTotais.pedagios.count} lançamento(s)
                            </small>
                          </div>
                        </div>
                        <div className="vec-fuel-kpi">
                          <FaMapMarkerAlt style={{ color: '#34d399' }} />
                          <div>
                            <label>Estacionamentos</label>
                            <strong>{formatCurrency(spTotais.estac.total)}</strong>
                            <small style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', display: 'block' }}>
                              {spTotais.estac.count} lançamento(s)
                            </small>
                          </div>
                        </div>
                        <div className="vec-fuel-kpi">
                          <FaFileInvoice style={{ color: '#c084fc' }} />
                          <div>
                            <label>Plano Contratado</label>
                            <strong>{formatCurrency(spTotais.plano.total)}</strong>
                            <small style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', display: 'block' }}>
                              {spTotais.plano.count} lançamento(s)
                            </small>
                          </div>
                        </div>
                      </div>

                      {/* Lista */}
                      <div className="vec-maintenance-list" style={{ marginTop: 4, height: 'auto', maxHeight: 'calc(100vh - 340px)' }}>
                        <h4 className="vec-section-title">
                          Lançamentos do Extrato Sem Parar
                          <span style={{ marginLeft: 8, fontWeight: 400, fontSize: '0.78rem', color: 'var(--text-muted, #64748b)' }}>
                            — Para importar: <strong>Ações → Sem Parar → Importar extrato</strong>
                          </span>
                        </h4>

                        <div className="vec-history-scroll">
                          {semPararData.rows.length === 0 ? (
                            <div className="vec-empty-box" style={{ textAlign: 'center', lineHeight: 1.7 }}>
                              <FaRoad style={{ fontSize: '2rem', color: '#475569', marginBottom: 10 }} />
                              <p style={{ margin: '0 0 4px' }}>Nenhum lançamento Sem Parar para este veículo.</p>
                              <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted, #64748b)' }}>
                                Importe o extrato pelo menu <strong>Ações → Sem Parar → Importar extrato</strong>.
                              </p>
                            </div>
                          ) : semPararData.rows.map((sp) => {
                            const isToll  = sp.tipo_uso === 'PASSAGENS';
                            const isEstac = sp.tipo_uso === 'ESTACIONAMENTO';

                            return (
                              <div key={sp.id} className="vec-log-card">
                                {/* Header do card */}
                                <div className="vec-log-card__header">
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <SpTipoUsoBadge tipo={sp.tipo_uso} />
                                    {sp.numero_fatura && (
                                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)' }}>
                                        Fatura #{sp.numero_fatura}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    className="vec-icon-btn delete"
                                    title="Excluir lançamento"
                                    onClick={() => handleDeleteSemParar(sp.id)}
                                  >
                                    <FaTrash />
                                  </button>
                                </div>

                                {/* Corpo do card */}
                                <div className="vec-log-card__body">
                                  <div className="vec-log-card__row">
                                    <FaCalendarAlt className="vec-log-icon" />
                                    <span><strong>Data/hora:</strong> {formatDateTime(sp.data_transacao)}</span>
                                  </div>

                                  {sp.descricao && (
                                    <div className="vec-log-card__row">
                                      <FaMapMarkerAlt className="vec-log-icon" style={{ color: isToll ? '#60a5fa' : isEstac ? '#34d399' : '#94a3b8' }} />
                                      <span className="vec-log-card__desc">{sp.descricao}</span>
                                    </div>
                                  )}

                                  {isToll && sp.sentido && (
                                    <div className="vec-log-card__row">
                                      <FaRoad className="vec-log-icon" style={{ color: '#60a5fa' }} />
                                      <span>
                                        <strong>Sentido:</strong>{' '}
                                        <span style={{ color: '#93c5fd' }}>
                                          {sp.sentido === 'Norte' ? '↑' : sp.sentido === 'Sul' ? '↓' : sp.sentido === 'Leste' ? '→' : sp.sentido === 'Oeste' ? '←' : ''}{' '}
                                          {sp.sentido}
                                        </span>
                                      </span>
                                    </div>
                                  )}

                                  {sp.placa_extrato && sp.placa_extrato !== veiculo.placa && (
                                    <div className="vec-log-card__row">
                                      <FaCar className="vec-log-icon" style={{ color: '#f59e0b' }} />
                                      <span style={{ fontSize: '0.78rem', color: '#fbbf24' }}>
                                        Placa no extrato: <strong>{sp.placa_extrato}</strong>
                                      </span>
                                    </div>
                                  )}

                                  {sp.viagem_vale_pedagio === 1 && (
                                    <div className="vec-log-card__row">
                                      <FaCheckCircle className="vec-log-icon" style={{ color: '#10b981' }} />
                                      <span style={{ fontSize: '0.78rem', color: '#6ee7b7' }}>Viagem de Vale Pedágio</span>
                                    </div>
                                  )}
                                </div>

                                {/* Rodapé com valor */}
                                <div className="vec-log-card__footer">
                                  {sp.debito_credito === 'CR' && (
                                    <span style={{ fontSize: '0.72rem', color: '#34d399', marginRight: 8 }}>Crédito/Estorno</span>
                                  )}
                                  <span className="vec-log-card__valor" style={{ color: sp.debito_credito === 'CR' ? '#34d399' : undefined }}>
                                    {sp.debito_credito === 'CR' ? '+' : ''}{formatCurrency(sp.valor)}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ══════════════════════════════════════════
                      SUB-ABA: MANUTENÇÕES FROTA
                  ══════════════════════════════════════════ */}
                  {logSubTab === 'manutencoes_frota' && (
                    <div className="vec-fuel-wrapper">
                      <div className="vec-fuel-kpis vec-mf-kpis">
                        <div className="vec-fuel-kpi">
                          <FaWrench />
                          <div><label>Manutenções Registradas</label><strong>{manutencoesFrota.length}</strong></div>
                        </div>
                        <div className="vec-fuel-kpi">
                          <FaMoneyBillWave />
                          <div><label>Total em Serviços</label><strong>{formatCurrency(totalManutencoesFrota)}</strong></div>
                        </div>
                        <div className="vec-fuel-kpi">
                          <FaCheckCircle />
                          <div><label>Concluídas</label><strong>{manutencoesFrota.filter((m) => m.status === 'concluido').length}</strong></div>
                        </div>
                      </div>

                      <div className="vec-maintenance-list" style={{ marginTop: 4, height: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
                        <h4 className="vec-section-title">Manutenções do Veículo</h4>
                        <div className="vec-history-scroll">
                          {manutencoesFrota.length === 0 ? (
                            <div className="vec-empty-box">Nenhuma manutenção registrada no módulo de Oficinas.</div>
                          ) : manutencoesFrota.map((m) => (
                            <div
                              key={m.id}
                              className="vec-log-card vec-log-card--clickable"
                              onClick={() => setManutencaoRelatorio(m)}
                              title="Clique para ver o relatório completo"
                            >
                              <div className="vec-log-card__header">
                                <span className="vec-log-card__id"><FaWrench /> Manutenção #{m.id}</span>
                                <MfStatusBadge status={m.status} />
                              </div>
                              <div className="vec-log-card__body">
                                <div className="vec-log-card__row"><FaTools className="vec-log-icon" /><span><strong>Serviço:</strong> {m.tipo_servico || '—'}</span></div>
                                {m.oficina_nome && (
                                  <div className="vec-log-card__row"><FaBuilding className="vec-log-icon" /><span><strong>Oficina:</strong> {m.oficina_nome}</span></div>
                                )}
                                <div className="vec-log-card__row">
                                  <FaCalendarAlt className="vec-log-icon" />
                                  <span><strong>Data:</strong> {formatDate(m.data_conclusao || m.data_abertura)}</span>
                                </div>
                                {m.descricao_problema && (
                                  <div className="vec-log-card__row"><FaFileInvoice className="vec-log-icon" /><span className="vec-log-card__desc">{m.descricao_problema}</span></div>
                                )}
                              </div>
                              <div className="vec-log-card__footer">
                                {m.arquivo_nf_url && (
                                  <a
                                    href={`${api.defaults.baseURL}/${String(m.arquivo_nf_url).replace(/\\/g, '/')}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="vec-icon-btn view" title="Abrir nota fiscal"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <FaDownload />
                                  </a>
                                )}
                                <span className="vec-log-card__valor">{formatCurrency(m.valor_final)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ══════════════════════════════════════════
                      SUB-ABA: ORDENS DE SERVIÇO
                  ══════════════════════════════════════════ */}
                  {logSubTab === 'os' && (
                    <div className="vec-log-scroll">
                      {logsUso.os.length === 0 ? (
                        <div className="vec-empty-box">Nenhuma OS para este veículo.</div>
                      ) : logsUso.os.map((os) => (
                        <div key={os.os_id} className="vec-log-card">
                          <div className="vec-log-card__header">
                            <span className="vec-log-card__id">OS #{String(os.os_id).padStart(4, '0')}</span>
                            <OsStatusBadge status={os.status} />
                          </div>
                          <div className="vec-log-card__body">
                            <div className="vec-log-card__row"><FaUser className="vec-log-icon" /><span><strong>Motorista:</strong> {os.motorista_nome}</span></div>
                            <div className="vec-log-card__row"><FaCalendarAlt className="vec-log-icon" /><span><strong>Criado:</strong> {formatDateTime(os.data_criacao)}</span></div>
                            {os.data_conclusao && (
                              <div className="vec-log-card__row">
                                <FaCheckCircle className="vec-log-icon" style={{ color: '#10b981' }} />
                                <span><strong>Concluído:</strong> {formatDateTime(os.data_conclusao)}</span>
                              </div>
                            )}
                            {os.descricao && (
                              <div className="vec-log-card__row"><FaFileInvoice className="vec-log-icon" /><span className="vec-log-card__desc">{os.descricao}</span></div>
                            )}
                          </div>
                          {os.valor > 0 && (
                            <div className="vec-log-card__footer"><span className="vec-log-card__valor">{formatCurrency(os.valor)}</span></div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ══════════════════════════════════════════
                      SUB-ABA: PRESTAÇÕES APP
                  ══════════════════════════════════════════ */}
                  {logSubTab === 'prestacoes' && (
                    <div className="vec-log-scroll">
                      {logsUso.prestacoes.length === 0 ? (
                        <div className="vec-empty-box">Nenhuma prestação via app para este veículo.</div>
                      ) : logsUso.prestacoes.map((p) => (
                        <div key={p.id} className="vec-log-card">
                          <div className="vec-log-card__header">
                            <span className="vec-log-card__id"><FaMobile /> Prestação #{p.id}</span>
                            <StatusBadge status={p.status} origem="app" />
                          </div>
                          <div className="vec-log-card__body">
                            <div className="vec-log-card__row"><FaUser className="vec-log-icon" /><span><strong>Motorista:</strong> {p.motorista_nome}</span></div>
                            <div className="vec-log-card__row"><FaCalendarAlt className="vec-log-icon" /><span><strong>Data:</strong> {formatDate(p.data)}</span></div>
                            <div className="vec-log-card__row"><FaFileInvoice className="vec-log-icon" /><span className="vec-log-card__desc">{p.descricao}</span></div>
                          </div>
                          <div className="vec-log-card__footer"><span className="vec-log-card__valor">{formatCurrency(p.valor)}</span></div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ══════════════════════════════════════════
                      SUB-ABA: USO DIÁRIO
                  ══════════════════════════════════════════ */}
                  {logSubTab === 'uso_diario' && (
                    <div className="vec-maintenance-layout" style={{ marginTop: 4 }}>
                      <div className="vec-maintenance-form">
                        <h4 className="vec-section-title">
                          Registrar Uso Diário
                          {usosEmAberto > 0 && (
                            <span className="vec-history-legend" style={{ marginLeft: 8 }}>
                              <span className="vec-origem-badge vec-origem-badge--status-pendente">
                                <FaClock style={{ marginRight: 4 }} /> {usosEmAberto} em aberto
                              </span>
                            </span>
                          )}
                        </h4>
                        <form onSubmit={handleAddUsoDiario} className="vec-form vec-form-compact">
                          <div className="vec-input-group">
                            <label>Motorista</label>
                            <select name="motorista_id" value={novoUso.motorista_id} onChange={handleNovoUsoChange} required>
                              <option value="">Selecione...</option>
                              {motoristasNormalizados.map((m) => (
                                <option key={m.optionKey} value={m.id_normalizado}>{m.nome}{m.label_extra}</option>
                              ))}
                            </select>
                          </div>
                          <div className="vec-form-compact-row">
                            <div className="vec-input-group">
                              <label>Data do uso</label>
                              <input type="date" name="data_uso" value={novoUso.data_uso} onChange={handleNovoUsoChange} required />
                            </div>
                            <div className="vec-input-group">
                              <label>Entrada</label>
                              <input type="datetime-local" name="entrada_em" value={novoUso.entrada_em} onChange={handleNovoUsoChange} required />
                            </div>
                          </div>
                          <div className="vec-input-group">
                            <label>Saída (Opcional)</label>
                            <input type="datetime-local" name="saida_em" value={novoUso.saida_em} onChange={handleNovoUsoChange} />
                          </div>
                          <div className="vec-input-group">
                            <label>Observação (Opcional)</label>
                            <input type="text" name="observacao" value={novoUso.observacao} onChange={handleNovoUsoChange} placeholder="Ex.: Plantão da manhã, rota externa..." />
                          </div>
                          <button type="submit" className="vec-btn-primary full-width" disabled={isSubmittingUso}>
                            {isSubmittingUso ? <><FaSpinner className="fa-spin" /> Salvando...</> : <><FaPlus /> Registrar Uso</>}
                          </button>
                        </form>
                      </div>

                      <div className="vec-maintenance-list">
                        <h4 className="vec-section-title">Histórico de Uso Diário</h4>
                        <div className="vec-history-scroll">
                          {usoDiario.length === 0 ? (
                            <div className="vec-empty-box">Nenhum uso diário registrado.</div>
                          ) : usoDiario.map((u) => (
                            <div key={u.id} className="vec-history-card">
                              <div className="vec-h-left">
                                <span className="vec-h-date"><FaCalendarAlt /> {formatDate(u.data_uso)}</span>
                                <strong>{u.motorista_nome || 'Não informado'}</strong>
                                <span className="vec-h-desc">
                                  <FaPlayCircle style={{ marginRight: 6, color: '#16a34a' }} />
                                  Entrada: {formatDateTime(u.entrada_em)}
                                </span>
                                <span className="vec-h-desc">
                                  <FaStopCircle style={{ marginRight: 6, color: u.saida_em ? '#dc2626' : '#94a3b8' }} />
                                  Saída: {u.saida_em ? formatDateTime(u.saida_em) : 'Em aberto'}
                                </span>
                                {u.observacao && <span className="vec-h-desc">{u.observacao}</span>}
                              </div>
                              <div className="vec-h-right">
                                <div className="vec-h-actions">
                                  {!u.saida_em && (
                                    <>
                                      <button
                                        type="button"
                                        className={`vec-icon-btn vec-icon-btn-ping ${pingSentUsoIds[u.id] ? 'is-sent-pulse' : ''} ${pingingUsoIds[u.id] ? 'is-sending' : ''}`}
                                        title={pingSentUsoIds[u.id] ? 'Mensagem enviada' : 'Enviar mensagem'}
                                        onClick={() => handleEnviarPingUso(u)}
                                        disabled={!!pingingUsoIds[u.id]}
                                      >
                                        {pingingUsoIds[u.id] ? <FaSpinner className="fa-spin" /> : <FaPaperPlane />}
                                      </button>
                                      <button type="button" className="vec-icon-btn view" title="Registrar saída agora" onClick={() => handleRegistrarSaidaAgora(u)}>
                                        <FaClock />
                                      </button>
                                    </>
                                  )}
                                  <button type="button" className="vec-icon-btn delete" title="Excluir registro" onClick={() => handleDeleteUsoDiario(u.id)}>
                                    <FaTrash />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════
              ABA: HISTÓRICO & DESPESAS
          ════════════════════════════════════════════════ */}
          {activeTab === 'manutencoes' && (
            <div className="vec-maintenance-wrapper animate-fade-in">
              <div className="vec-maintenance-kpi">
                <div className="vec-kpi-icon"><FaMoneyBillWave /></div>
                <div className="vec-kpi-data">
                  <label>Custo Total Consolidado (Despesas + Abastecimento + Oficina)</label>
                  <strong>{formatCurrency(totalGastoManutencao)}</strong>
                </div>
              </div>

              <div className="vec-maintenance-layout">
                <div className="vec-maintenance-form">
                  <h4 className="vec-section-title">Lançar Nova Despesa</h4>
                  <form onSubmit={handleAddMaintenance} className="vec-form vec-form-compact">
                    <div className="vec-form-compact-row">
                      <div className="vec-input-group">
                        <label>Data</label>
                        <input type="date" name="data" value={newMaintenance.data} onChange={handleMaintenanceChange} required />
                      </div>
                      <div className="vec-input-group">
                        <label>Custo (R$)</label>
                        <input type="number" step="0.01" name="custo" value={newMaintenance.custo} onChange={handleMaintenanceChange} required className="vec-input-money" placeholder="0.00" />
                      </div>
                    </div>
                    <div className="vec-input-group">
                      <label>Serviço / Peça</label>
                      <input type="text" name="tipo" value={newMaintenance.tipo} onChange={handleMaintenanceChange} required placeholder="Ex: Troca de Óleo" />
                    </div>
                    <div className="vec-input-group">
                      <label>Descrição (Opcional)</label>
                      <input type="text" name="descricao" value={newMaintenance.descricao} onChange={handleMaintenanceChange} placeholder="Detalhes..." />
                    </div>
                    <div className="vec-input-group">
                      <label>Anexo (NF/Recibo)</label>
                      <div className="vec-upload-minimalist">
                        <input id="file-upload-maintenance" type="file" name="anexo" onChange={handleFileChange} style={{ display: 'none' }} accept="image/*,.pdf" />
                        <label htmlFor="file-upload-maintenance" className={`vec-upload-label-mini ${anexo ? 'has-file' : ''}`}>
                          <FaPaperclip className="upload-icon-mini" />
                          <span className="upload-text-mini">{anexo ? anexo.name : 'Anexar comprovante...'}</span>
                        </label>
                        {anexo && (
                          <button type="button" className="vec-remove-file-mini" onClick={() => setAnexo(null)}><FaTimes /></button>
                        )}
                      </div>
                    </div>
                    <button type="submit" className="vec-btn-danger full-width" disabled={isSubmitting}>
                      {isSubmitting ? <><FaSpinner className="fa-spin" /> Registrando...</> : <><FaPlus /> Lançar Despesa</>}
                    </button>
                  </form>
                </div>

                <div className="vec-maintenance-list">
                  <h4 className="vec-section-title">
                    Histórico Registrado
                    <span className="vec-history-legend">
                      <span className="vec-origem-badge vec-origem-badge--app"><FaMobile /> App</span>
                      <span className="vec-origem-badge vec-origem-badge--fuel"><FaGasPump /> Combustível</span>
                      <span className="vec-origem-badge vec-origem-badge--oficina"><FaWrench /> Oficina</span>
                    </span>
                  </h4>
                  {isLoadingHistory ? (
                    <div className="vec-loading-box"><FaSpinner className="fa-spin" /></div>
                  ) : (
                    <div className="vec-history-scroll">
                      {historicoManutencao.length > 0 ? historicoManutencao.map((m) => (
                        <div key={`${m.origem}-${m.id}`} className="vec-history-card">
                          <div className="vec-h-left">
                            <span className="vec-h-date"><FaCalendarAlt /> {formatDate(m.data)}</span>
                            <strong>{m.tipo}</strong>
                            {m.descricao && <span className="vec-h-desc">{m.descricao}</span>}
                          </div>
                          <div className="vec-h-right">
                            <span className="vec-h-cost">{formatCurrency(m.custo)}</span>
                            <div className="vec-h-actions">
                              <OrigemBadge origem={m.origem} />
                              <StatusBadge status={m.status_aprovacao} origem={m.origem} />
                              {m.anexo_path && (
                                <a href={`${api.defaults.baseURL}/${m.anexo_path.replace(/\\/g, '/')}`} target="_blank" rel="noopener noreferrer" className="vec-icon-btn view">
                                  <FaDownload />
                                </a>
                              )}
                              {m.origem === 'manutencao' && (
                                <button type="button" onClick={() => handleDeleteMaintenance(m.id)} className="vec-icon-btn delete">
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )) : (
                        <div className="vec-empty-box">Nenhuma despesa ou manutenção registrada.</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>{/* /vec-edit-body */}
      </div>{/* /vec-edit-modal */}

      {/* ── Modal aninhado: Relatório de Manutenção ── */}
      {manutencaoRelatorio && (
        <ManutencaoRelatorioModal
          manutencao={manutencaoRelatorio}
          veiculo={veiculo}
          onClose={() => setManutencaoRelatorio(null)}
        />
      )}
    </div>
  );
}